import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const RegulatoryCompliance = () => {
  // Compliance principles from the content
  const compliancePrinciples = [
    {
      title: "Transparent Pricing Mechanisms",
      description: "Clear, upfront disclosure of all charges and fees with no hidden costs",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 9a2 2 0 10-4 0v5a2 2 0 01-2 2h6m-6-4h4m8 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Responsible Customer Onboarding",
      description: "Thorough verification and assessment to ensure suitable financial solutions",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      )
    },
    {
      title: "Ethical Recovery Practices",
      description: "Respectful communication and fair collection processes",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    }
  ];

  // Industry standards and principles
  const industryStandards = [
    "Microfinance best practices",
    "Fair lending principles",
    "Customer protection standards",
    "Data privacy guidelines"
  ];

  // Core values
  const coreValues = [
    { value: "Fair", icon: "⚖️" },
    { value: "Structured", icon: "📋" },
    { value: "Customer-Friendly", icon: "🤝" },
    { value: "Compliant", icon: "✅" }
  ];

  return (
    <section className="relative w-full min-h-screen bg-[#0A1620] overflow-hidden">
      {/* Dark gradient background (same as HeroSection) */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[#0A1620]"></div>
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#1B4D5E]/20 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#1A3B4D]/20 rounded-full blur-[100px]"></div>
        
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #2D5F7A 1px, transparent 1px), linear-gradient(to bottom, #2D5F7A 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>

        {/* Wave decorations */}
        <svg className="absolute top-0 left-0 w-full h-48 rotate-180" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#2D5F7A" fillOpacity="0.08" d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>

        <svg className="absolute bottom-0 right-0 w-full h-48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#2D5F7A" fillOpacity="0.08" d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>

      {/* Main Content */}
      <div className="relative z-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full py-24 lg:py-28">
          
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-[#1E3B4A]/30 px-4 py-2 rounded-full border border-[#2D5F7A]/30 backdrop-blur-sm mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4D9ABF] opacity-40"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4D9ABF]"></span>
              </span>
              <span className="text-[#B2D9F0] text-xs font-bold uppercase tracking-wider">
                COMPLIANCE • ETHICS • TRANSPARENCY
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Regulatory <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF]">Approach</span>
            </h1>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-[#CCE6F5] font-light max-w-3xl mx-auto leading-relaxed"
            >
              SalaryMates operates with a strong focus on compliance-oriented practices and aims to align 
              with industry standards followed in the financial services sector.
            </motion.p>
          </motion.div>

          {/* Core Values Strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {coreValues.map((item, index) => (
              <div key={index} className="bg-[#1E3B4A]/30 rounded-full px-4 py-2 border border-[#2D5F7A]/30 flex items-center gap-2">
                <span className="text-lg">{item.icon}</span>
                <span className="text-[#CCE6F5] text-sm font-medium">{item.value}</span>
              </div>
            ))}
          </motion.div>

          {/* Main Principles Grid */}
          <motion.div
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.2, delayChildren: 0.5 }
              }
            }}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-3 gap-6 mb-12"
          >
            {compliancePrinciples.map((principle, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0 }
                }}
                className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-6 border border-[#2D5F7A]/30 hover:border-[#7BC4E0]/50 transition-all duration-300 group"
              >
                <div className="w-16 h-16 rounded-full bg-[#1E3B4A] flex items-center justify-center mb-4 border border-[#2D5F7A]/30 group-hover:border-[#7BC4E0]/50 transition-all">
                  <div className="text-[#7BC4E0] group-hover:scale-110 transition-transform">
                    {principle.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{principle.title}</h3>
                <p className="text-[#8FAFC7] text-sm leading-relaxed">{principle.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Framework Section */}
          <div className="grid lg:grid-cols-2 gap-6 mb-12">
            {/* Industry Standards */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-6 border border-[#2D5F7A]/30"
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-[#7BC4E0] rounded-full"></span>
                Industry Standards We Follow
              </h3>
              <p className="text-[#CCE6F5] text-sm mb-4">
                Our internal framework is inspired by widely accepted principles in microfinance and financial assistance models:
              </p>
              <div className="space-y-3">
                {industryStandards.map((standard, index) => (
                  <div key={index} className="flex items-center gap-3 bg-[#1E3B4A]/20 rounded-lg p-3 border border-[#2D5F7A]/20">
                    <div className="w-2 h-2 bg-[#7BC4E0] rounded-full"></div>
                    <span className="text-[#CCE6F5] text-sm">{standard}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Commitment Statement */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
              className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-6 border border-[#2D5F7A]/30"
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-[#7BC4E0] rounded-full"></span>
                Our Commitment
              </h3>
              
              <div className="mb-6">
                <p className="text-[#CCE6F5] text-sm leading-relaxed mb-4">
                  We continuously work towards strengthening our processes to ensure that our services 
                  remain fair, structured, and customer-friendly.
                </p>
              </div>

              {/* Progress Statement */}
              <div className="bg-[#1E3B4A]/30 rounded-lg p-4 border border-[#2D5F7A]/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#7BC4E0] text-sm font-medium">Process Strengthening</span>
                  <span className="text-white text-sm font-bold">Ongoing</span>
                </div>
                <div className="w-full h-2 bg-[#1E3B4A] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "85%" }}
                    transition={{ duration: 1, delay: 1.2 }}
                    className="h-full bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF] rounded-full"
                  />
                </div>
                <p className="text-[#8FAFC7] text-xs mt-2">Continuously improving our compliance framework</p>
              </div>
            </motion.div>
          </div>

          {/* Compliance Badges */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-8 border border-[#2D5F7A]/30"
          >
            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-[#1E3B4A] flex items-center justify-center border border-[#7BC4E0]/30">
                  <svg className="w-8 h-8 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-[#CCE6F5] text-xs">Compliant</span>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-[#1E3B4A] flex items-center justify-center border border-[#7BC4E0]/30">
                  <svg className="w-8 h-8 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-[#CCE6F5] text-xs">Ethical</span>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-[#1E3B4A] flex items-center justify-center border border-[#7BC4E0]/30">
                  <svg className="w-8 h-8 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <span className="text-[#CCE6F5] text-xs">Structured</span>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-[#1E3B4A] flex items-center justify-center border border-[#7BC4E0]/30">
                  <svg className="w-8 h-8 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <span className="text-[#CCE6F5] text-xs">Customer-Friendly</span>
              </div>
            </div>
          </motion.div>

          {/* Footer CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="text-center mt-12"
          >
            <Link
              to="/fair-practice-code"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2D5F7A] to-[#1E3B4A] text-white font-semibold rounded-lg shadow-lg hover:shadow-[#2D5F7A]/30 transition-all duration-300 hover:scale-105 border border-[#7BC4E0]/20"
            >
              View Our Fair Practice Code
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Bottom decorative text */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        <span className="text-[#2D5F7A] text-sm">COMPLIANT</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#7BC4E0] text-sm">ETHICAL</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#2D5F7A] text-sm">TRANSPARENT</span>
      </div>
    </section>
  );
};

export default RegulatoryCompliance;